// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the connector card used wherever we ask someone to link Microsoft 365
// or ClickUp — the settings tab, and the calendar / mail / tasks empty states.

import styles from '@app/features/konvo_connections/components/KonvoConnectorCard.module.css';
import {Button, type ButtonVariant} from '@app/features/ui/button/Button';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {clsx} from 'clsx';
import type React from 'react';

const CONNECTED_DESCRIPTOR = msg({message: 'Connected', comment: 'Status pill on a connector card.'});
const NOT_CONNECTED_DESCRIPTOR = msg({
	message: 'Not connected',
	comment: 'Status pill on a connector card that has not been linked yet.',
});

export interface KonvoConnectorCardProps {
	/** Brand mark, rendered inside the tinted tile. */
	logo: React.ReactNode;
	name: string;
	description: string;
	features?: ReadonlyArray<string>;
	connected: boolean;
	statusLabel?: string;
	/** Account the connection belongs to, when the API tells us. */
	account?: string | null;
	actionLabel: string;
	actionVariant?: ButtonVariant;
	onAction: () => void;
	/** Extra action shown under the primary one, e.g. Disconnect next to Reconnect. */
	secondaryActionLabel?: string;
	onSecondaryAction?: () => void;
	busy?: boolean;
	/** Replaces the actions, e.g. when the instance has no OAuth client configured. */
	note?: string;
	/** Trailing control on the actions row that stays when `note` replaces the actions (e.g. staff settings). */
	accessory?: React.ReactNode;
	solo?: boolean;
	'data-flx'?: string;
}

export function KonvoConnectorCard({
	logo,
	name,
	description,
	features,
	connected,
	statusLabel,
	account,
	actionLabel,
	actionVariant = 'primary',
	onAction,
	secondaryActionLabel,
	onSecondaryAction,
	busy = false,
	note,
	accessory,
	solo = false,
}: KonvoConnectorCardProps) {
	const {i18n} = useLingui();
	return (
		<div
			className={clsx(styles.card, connected && styles.cardConnected, solo && styles.cardSolo)}
			data-flx="konvo-connections.connector-card.card"
		>
			<div className={styles.top} data-flx="konvo-connections.connector-card.top">
				<span className={styles.logoTile} data-flx="konvo-connections.connector-card.logo-tile">
					{logo}
				</span>
				<div className={styles.heading} data-flx="konvo-connections.connector-card.heading">
					<p className={styles.name} data-flx="konvo-connections.connector-card.name">
						{name}
					</p>
					{connected && account ? (
						<p className={styles.account} title={account} data-flx="konvo-connections.connector-card.account">
							{account}
						</p>
					) : null}
				</div>
				<span
					className={clsx(styles.status, connected && styles.statusConnected)}
					data-flx="konvo-connections.connector-card.status"
				>
					<span
						className={styles.statusDot}
						aria-hidden="true"
						data-flx="konvo-connections.connector-card.status-dot"
					/>
					{statusLabel ?? i18n._(connected ? CONNECTED_DESCRIPTOR : NOT_CONNECTED_DESCRIPTOR)}
				</span>
			</div>
			<p className={styles.description} data-flx="konvo-connections.connector-card.description">
				{description}
			</p>
			{features && features.length > 0 ? (
				<div className={styles.features} data-flx="konvo-connections.connector-card.features">
					{features.map((feature) => (
						<span key={feature} className={styles.feature} data-flx="konvo-connections.connector-card.feature">
							{feature}
						</span>
					))}
				</div>
			) : null}
			<div className={styles.actions} data-flx="konvo-connections.connector-card.actions">
				{note ? (
					<p className={styles.note} data-flx="konvo-connections.connector-card.note">
						{note}
					</p>
				) : (
					<>
						<Button
							variant={actionVariant}
							small={!solo}
							fitContainer={solo}
							submitting={busy}
							onClick={onAction}
							data-flx="konvo-connections.connector-card.action"
						>
							{actionLabel}
						</Button>
						{secondaryActionLabel && onSecondaryAction ? (
							<Button
								variant="ghost"
								small={!solo}
								fitContainer={solo}
								submitting={busy}
								onClick={onSecondaryAction}
								data-flx="konvo-connections.connector-card.secondary-action"
							>
								{secondaryActionLabel}
							</Button>
						) : null}
					</>
				)}
				{accessory ? <span className={styles.accessory}>{accessory}</span> : null}
			</div>
		</div>
	);
}
